import { Image as ImageIcon, Sparkles, Download, Cpu, Ratio, Layers, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import noCloudImg from '../../../assets/no_cloud.jpg';
import noCloud2Img from '../../../assets/no_cloud2.png';

interface ImageGenScreenProps {
  className?: string;
}

interface Tile {
  file: string;
  gradient: string;
  /** Optional real generated image served from /public/demo. */
  src?: string;
}

const TILES: readonly Tile[] = [
  {
    file: 'rechenzentrum-server-ki.jpg',
    src: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&h=800&fit=crop&q=85',
    gradient: 'linear-gradient(135deg, #0A2A4F 0%, #2D7EFF 55%, #00D4FF 100%)'
  },
  { file: 'no_cloud.jpg', src: noCloudImg, gradient: 'linear-gradient(120deg, #07304A 0%, #0E7C8C 50%, #34D399 110%)' },
  { file: 'no_cloud2.png', src: noCloud2Img, gradient: 'linear-gradient(150deg, #2A1457 0%, #2D7EFF 60%, #7C3AED 100%)' },
];

const PREFIX = 'bnk-imggen';

export function ImageGenScreen({ className }: ImageGenScreenProps) {
  const { t } = useTranslation('demo');

  const CHIPS: readonly { icon: typeof Cpu; label: string; value: string }[] = [
    { icon: Cpu, label: t('imagegen.chips.model'), value: 'SDXL-Local' },
    { icon: Ratio, label: t('imagegen.chips.aspectRatio'), value: '1:1' },
    { icon: Layers, label: t('imagegen.chips.steps'), value: '30' },
  ];

  return (
    <div className={`${PREFIX}-root${className ? ` ${className}` : ''}`}>
      <style>{`
        .${PREFIX}-root { font-family: Inter, system-ui, sans-serif; color: #E2E8F0; display: flex; flex-direction: column; gap: 20px; padding: 4px; }
        .${PREFIX}-headrow { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
        .${PREFIX}-pill { width: 40px; height: 40px; border-radius: 12px; background: rgba(45,126,255,0.10); border: 1px solid #1E2D45; display: grid; place-items: center; color: #2D7EFF; flex-shrink: 0; }
        .${PREFIX}-title { font-size: 20px; font-weight: 700; color: #E2E8F0; margin: 0; line-height: 1.1; }
        .${PREFIX}-sub { font-size: 13px; color: #94A3C2; margin: 2px 0 0; }
        .${PREFIX}-badge { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; color: #34D399; background: rgba(52,211,153,0.10); border: 1px solid rgba(52,211,153,0.30); border-radius: 999px; padding: 5px 11px; }
        .${PREFIX}-dot { width: 7px; height: 7px; border-radius: 50%; background: #34D399; animation: ${PREFIX}-pulse 1.8s ease-in-out infinite; }
        @keyframes ${PREFIX}-pulse { 0%,100% { opacity: 1; box-shadow: 0 0 0 0 rgba(52,211,153,0.5); } 50% { opacity: 0.55; box-shadow: 0 0 0 5px rgba(52,211,153,0); } }
        .${PREFIX}-card { background: #141B27; border: 1px solid #1E2D45; border-radius: 16px; padding: 16px; box-shadow: 0 1px 2px rgba(0,0,0,0.4); }
        .${PREFIX}-promptrow { display: flex; gap: 12px; align-items: stretch; }
        .${PREFIX}-textarea { flex: 1; resize: none; background: #0A0F1C; border: 1px solid #1E2D45; border-radius: 12px; color: #E2E8F0; font-family: inherit; font-size: 14px; line-height: 1.5; padding: 12px 14px; min-height: 76px; outline: none; }
        .${PREFIX}-textarea::placeholder { color: #4A5568; }
        .${PREFIX}-textarea:focus { border-color: #30415F; }
        .${PREFIX}-gen { align-self: stretch; display: inline-flex; align-items: center; gap: 8px; background: #2D7EFF; color: #fff; border: none; border-radius: 12px; font-family: inherit; font-size: 14px; font-weight: 600; padding: 0 20px; cursor: pointer; white-space: nowrap; transition: background 0.15s; }
        .${PREFIX}-gen:hover { background: #1F6BEA; }
        .${PREFIX}-chiprow { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 12px; }
        .${PREFIX}-chip { display: inline-flex; align-items: center; gap: 8px; font-size: 12px; color: #94A3C2; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 999px; padding: 6px 12px; }
        .${PREFIX}-chip strong { color: #E2E8F0; font-weight: 600; }
        .${PREFIX}-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 14px; }
        .${PREFIX}-tile { position: relative; border-radius: 14px; overflow: hidden; border: 1px solid #1E2D45; aspect-ratio: 1 / 1; }
        .${PREFIX}-tile-img { position: absolute; inset: 0; }
        .${PREFIX}-tile-scan { position: absolute; inset: 0; background: linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.18) 50%, transparent 70%); background-size: 250% 100%; animation: ${PREFIX}-scan 3.4s linear infinite; mix-blend-mode: screen; }
        @keyframes ${PREFIX}-scan { 0% { background-position: 150% 0; } 100% { background-position: -150% 0; } }
        .${PREFIX}-tile-cap { position: absolute; left: 0; right: 0; bottom: 0; display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 9px 11px; background: linear-gradient(to top, rgba(10,15,28,0.92), transparent); font-size: 11.5px; color: #94A3C2; }
        .${PREFIX}-tile-cap span { font-variant-numeric: tabular-nums; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .${PREFIX}-dl { display: grid; place-items: center; width: 28px; height: 28px; border-radius: 8px; flex-shrink: 0; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); color: #E2E8F0; cursor: pointer; opacity: 0; transition: opacity 0.15s, background 0.15s; }
        .${PREFIX}-tile:hover .${PREFIX}-dl { opacity: 1; }
        .${PREFIX}-dl:hover { background: rgba(45,126,255,0.30); }
        .${PREFIX}-section { font-size: 13px; font-weight: 600; color: #94A3C2; margin: 0 0 12px; display: flex; align-items: center; gap: 8px; }

        /* ── light-mode overrides (image tiles stay) ── */
        :root:not(.dark) .${PREFIX}-root,
        :root:not(.dark) .${PREFIX}-title,
        :root:not(.dark) .${PREFIX}-textarea,
        :root:not(.dark) .${PREFIX}-chip strong { color: #0f172a; }
        :root:not(.dark) .${PREFIX}-sub,
        :root:not(.dark) .${PREFIX}-chip,
        :root:not(.dark) .${PREFIX}-section { color: #475569; }
        :root:not(.dark) .${PREFIX}-textarea::placeholder { color: #94a3b8; }
        :root:not(.dark) .${PREFIX}-card { background: #f8fafc; border-color: #e2e8f0; }
        :root:not(.dark) .${PREFIX}-pill { border-color: #e2e8f0; }
        :root:not(.dark) .${PREFIX}-textarea { background: #eef2f7; border-color: #e2e8f0; }
        :root:not(.dark) .${PREFIX}-textarea:focus { border-color: #cbd5e1; }
        :root:not(.dark) .${PREFIX}-chip { background: rgba(15,23,42,0.04); border-color: rgba(15,23,42,0.08); }
      `}</style>

      <div className={`${PREFIX}-headrow`}>
        <div className={`${PREFIX}-pill`} aria-hidden="true">
          <ImageIcon size={20} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 className={`${PREFIX}-title`}>{t('imagegen.title')}</h1>
          <p className={`${PREFIX}-sub`}>{t('imagegen.subtitle')}</p>
        </div>
        <span className={`${PREFIX}-badge`}>
          <span className={`${PREFIX}-dot`} aria-hidden="true" />
          <ShieldCheck size={13} aria-hidden="true" />
          {t('imagegen.badge')}
        </span>
      </div>

      <div className={`${PREFIX}-card`}>
        <div className={`${PREFIX}-promptrow`}>
          <textarea
            className={`${PREFIX}-textarea`}
            aria-label={t('imagegen.promptAriaLabel')}
            defaultValue={t('imagegen.promptDefault')}
          />
          <button type="button" className={`${PREFIX}-gen`}>
            <Sparkles size={16} aria-hidden="true" />
            {t('imagegen.generateButton')}
          </button>
        </div>
        <div className={`${PREFIX}-chiprow`}>
          {CHIPS.map(({ icon: Icon, label, value }) => (
            <span key={label} className={`${PREFIX}-chip`}>
              <Icon size={13} aria-hidden="true" />
              {label}: <strong>{value}</strong>
            </span>
          ))}
        </div>
      </div>

      <div>
        <h2 className={`${PREFIX}-section`}>
          <Sparkles size={14} aria-hidden="true" />
          {t('imagegen.generatedImages')}
        </h2>
        <div className={`${PREFIX}-grid`}>
          {TILES.map((tile) => (
            <figure key={tile.file} className={`${PREFIX}-tile`} style={{ margin: 0 }}>
              {tile.src ? (
                <img
                  className={`${PREFIX}-tile-img`}
                  src={tile.src}
                  alt={t('imagegen.tileAlt', { file: tile.file })}
                  loading="lazy"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <div className={`${PREFIX}-tile-img`} style={{ background: tile.gradient }} role="img" aria-label={t('imagegen.tileAlt', { file: tile.file })} />
              )}
              <div className={`${PREFIX}-tile-scan`} aria-hidden="true" />
              <figcaption className={`${PREFIX}-tile-cap`}>
                <span>{tile.file}</span>
                <button type="button" className={`${PREFIX}-dl`} aria-label={t('imagegen.downloadAriaLabel', { file: tile.file })}>
                  <Download size={14} aria-hidden="true" />
                </button>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </div>
  );
}
